/**
 * Header Component
 * Application header with logo, navigation, and language selector
 *
 * Features:
 * - Notification bell with badge count
 * - User avatar menu with dropdown
 * - Language selector
 * - Responsive navigation
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { Check, Bell, ChevronDown, User, Settings, LogOut } from 'lucide-react';
import { LanguageSelector } from '../common/LanguageSelector';
import { useTranslation } from 'react-i18next';

export interface HeaderProps {
  logo?: ReactNode;
  logoText?: string;
  navigation?: NavigationItem[];
  showLanguageSelector?: boolean;
  userMenu?: ReactNode;
  onLogoClick?: () => void;
  className?: string;
  /** Notification configuration */
  notifications?: {
    count: number;
    onClick?: () => void;
  };
  /** User information for avatar menu */
  user?: {
    name: string;
    email?: string;
    avatar?: string;
    role?: string;
    onProfileClick?: () => void;
    onSettingsClick?: () => void;
    onLogoutClick?: () => void;
  };
}

export interface NavigationItem {
  label: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  badge?: string | number;
}

export function Header({
  logo,
  logoText = 'HALMAS',
  navigation = [],
  showLanguageSelector = true,
  userMenu,
  onLogoClick,
  className = '',
  notifications,
  user,
}: HeaderProps) {
  const { t } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  return (
    <header
      className={`bg-white border-b border-gray-200 sticky top-0 z-40 ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo Section */}
          <div className="flex items-center">
            <button
              onClick={onLogoClick}
              className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
              aria-label={t('navigation.home', 'Home')}
            >
              {logo || (
                <div className="h-8 w-8 bg-gradient-to-br from-green-600 to-blue-600 rounded flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-gray-900">{logoText}</span>
            </button>
          </div>

          {/* Navigation */}
          {navigation.length > 0 && (
            <nav className="hidden md:flex space-x-1" aria-label="Main navigation">
              {navigation.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                  className={`
                    relative px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${
                      item.active
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                  `}
                  aria-current={item.active ? 'page' : undefined}
                >
                  {item.label}
                  {item.badge !== undefined && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {item.badge}
                    </span>
                  )}
                </a>
              ))}
            </nav>
          )}

          {/* Right Section: Notifications, Language Selector & User Menu */}
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            {notifications && (
              <button
                onClick={notifications.onClick}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label={`${notifications.count} notifications`}
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {notifications.count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-error-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1 font-medium">
                    {notifications.count > 99 ? '99+' : notifications.count}
                  </span>
                )}
              </button>
            )}

            {/* Language Selector */}
            {showLanguageSelector && (
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>
            )}

            {/* User Avatar Menu */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                >
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    {user.role && (
                      <p className="text-xs text-gray-500">{user.role}</p>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-600 hidden md:block" />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsUserMenuOpen(false)}
                    />

                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        {user.email && (
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        )}
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {user.onProfileClick && (
                          <button
                            onClick={() => {
                              user.onProfileClick?.();
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <User className="w-4 h-4" />
                            {t('common:navigation.profile', 'Profile')}
                          </button>
                        )}

                        {user.onSettingsClick && (
                          <button
                            onClick={() => {
                              user.onSettingsClick?.();
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Settings className="w-4 h-4" />
                            {t('common:navigation.settings', 'Settings')}
                          </button>
                        )}

                        {user.onLogoutClick && (
                          <>
                            <div className="my-1 border-t border-gray-200" />
                            <button
                              onClick={() => {
                                user.onLogoutClick?.();
                                setIsUserMenuOpen(false);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-error-600 hover:bg-error-50 transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              {t('common:navigation.logout', 'Logout')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Legacy userMenu prop (for backward compatibility) */}
            {userMenu && <div className="flex items-center">{userMenu}</div>}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {navigation.length > 0 && (
        <div className="md:hidden border-t border-gray-200">
          <nav className="px-2 py-2 space-y-1" aria-label="Mobile navigation">
            {navigation.map((item, index) => (
              <a
                key={index}
                href={item.href}
                onClick={(e) => {
                  if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
                className={`
                  block px-3 py-2 rounded-md text-base font-medium transition-colors
                  ${
                    item.active
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                aria-current={item.active ? 'page' : undefined}
              >
                <div className="flex items-center justify-between">
                  <span>{item.label}</span>
                  {item.badge !== undefined && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-600">
                      {item.badge}
                    </span>
                  )}
                </div>
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
